package com.iregained.site.components.sections.clinicians.sections

import androidx.compose.runtime.*
import com.iregained.site.components.sections.clinicians.styles.*
import com.iregained.site.theme.toSitePalette
import com.iregained.site.utils.LocalBreakpoint
import com.iregained.site.utils.Res
import com.varabyte.kobweb.browser.dom.observers.IntersectionObserver
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.css.TransitionTimingFunction
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.numColumns
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.ms
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.s
import org.jetbrains.compose.web.dom.Div

// UNIQUE ANIMATION: Staggered Opacity Pulse
@Composable
private fun Modifier.staggeredPulse(isVisible: Boolean, delayMs: Int): Modifier {
    return this.opacity(if (isVisible) 1 else 0).scale(if (isVisible) 1f else 0.9f).transition(
            Transition.of("opacity", 0.6.s, TransitionTimingFunction.EaseOut, delayMs.ms),
            Transition.of("transform", 0.6.s, TransitionTimingFunction.EaseOut, delayMs.ms)
        )
}

@Composable
fun CliniciansImpactBarSection(data: CliniciansImpactBarData = defaultCliniciansImpactBarData) {
    val breakpoint = LocalBreakpoint.current
    val isDesktop = breakpoint >= Res.Breakpoints.LG
    val targetId = data.sectionId?.takeIf { it.isNotBlank() } ?: "clinicians-impact-bar-section"
    var isVisible by remember { mutableStateOf(false) }
    val palette = ColorMode.current.toSitePalette()

    if (data.stats.isNotEmpty()) {
        Box(
            modifier = ImpactBarContainerStyle.toModifier().id(targetId)
                .backgroundColor(palette.brand.primarySoft)
                .padding(topBottom = if (isDesktop) 50.px else 25.px, leftRight = 5.percent)
                .styleModifier { property("scroll-margin-top", "100px") }, contentAlignment = Alignment.Center
        ) {
            Div(
                attrs = Modifier.fillMaxWidth().display(org.jetbrains.compose.web.css.DisplayStyle.Flex)
                    .justifyContent(org.jetbrains.compose.web.css.JustifyContent.Center).toAttrs {
                    ref { element ->
                        val observer = IntersectionObserver { entries, obs ->
                            entries.forEach {
                                if (it.isIntersecting) {
                                    isVisible = true; obs.unobserve(element)
                                }
                            }
                        }
                        observer.observe(element); onDispose { observer.disconnect() }
                    }
                }) {
                SimpleGrid(
                    numColumns(1, sm = 1, md = 3),
                    modifier = ImpactBarGridStyle.toModifier().maxWidth(1440.px).gap(40.px)
                ) {
                    data.stats.forEachIndexed { index, stat ->
                        key(stat.statLabel) {
                            ImpactStatItem(
                                stat, Modifier.staggeredPulse(isVisible, 200 + (index * 150)),
                                isDesktop = isDesktop
                            )
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun ImpactStatItem(stat: ImpactStatData, modifier: Modifier, isDesktop: Boolean) {
    Row(
        verticalAlignment = Alignment.CenterVertically,
        modifier = ImpactStatItemStyle.toModifier().then(modifier),
        horizontalArrangement = if (isDesktop) Arrangement.Center else Arrangement.Start
    ) {
        if (stat.iconPath.isNotBlank()) Image(src = stat.iconPath, modifier = ImpactStatIconStyle.toModifier())
        Column {
            if (stat.statValue.isNotBlank()) SpanText(
                text = stat.statValue,
                modifier = ImpactStatValueStyle.toModifier()
            )
            if (stat.statLabel.isNotBlank()) SpanText(
                text = stat.statLabel,
                modifier = ImpactStatLabelStyle.toModifier().fontSize(16.px).fontWeight(FontWeight.Medium)
            )
        }
    }
}

// ==========================================
// CMS DATA MODEL & IMAGE SPECS
// ==========================================
/**
 * CMS IMAGE UPLOAD SPECS FOR IMPACT BAR ICONS:
 * @field iconPath:
 * - Aspect Ratio: 1:1 (Square).
 * - Recommended Dimensions: 64x64px or 128x128px.
 * - Format: SVG (Highly Preferred) or transparent PNG.
 * - Content: Minimalist, monochromatic B2B iconography (e.g., a shield for evidence, an activity ring for repetition, a lightning bolt for setup). Line-art style preferred over filled icons.
 */
data class ImpactStatData(
    val iconPath: String, val statValue: String, val statLabel: String
)

data class CliniciansImpactBarData(
    val sectionId: String? = null, val stats: List<ImpactStatData> = listOf(
        ImpactStatData(
            iconPath = Res.Images.CliniciansPage.IMPACT.EVIDENCE,
            statValue = "Evidence-Based",
            statLabel = "Backed by rigorous clinical trials"
        ), ImpactStatData(
            iconPath = Res.Images.CliniciansPage.IMPACT.REPETITION,
            statValue = "Multi-Modal",
            statLabel = "Pre-programmed Protocols, Customizable Protocol & Gamification"
        ), ImpactStatData(
            iconPath = Res.Images.CliniciansPage.IMPACT.DISRUPTION,
            statValue = "Quick & Easy Setup",
            statLabel = "Less than a minute to set-up and configure Patient’s hand"
        )
    )
)

val defaultCliniciansImpactBarData = CliniciansImpactBarData()